import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import IntegrationSettingsTabs from "../../../../Components/Common/IntegrationSettingsTabs";
import ApiMessageHistoryCard from "../../../../Components/Common/ApiMessageHistoryCard";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import { parseApiMessagesQueryStatus } from "../../../../helpers/api_messages_helper";
import {
  ApiToken,
  ApiTokenScope,
  createApiToken,
  getApiTokens,
  revokeApiToken,
} from "../../../../helpers/api_tokens_helper";
import { skipReasonLabel } from "../../../../helpers/api_messages_helper";

type FormValues = {
  name: string;
  scopeContacts: boolean;
  scopeMessages: boolean;
};

/**
 * Hub de integração via API: tokens + histórico de envios avulsos (POST /messages).
 * Por quê junto: credencial e resultado da integração no mesmo mental model (não em Conta).
 */
const ApiTokensPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";
  const [searchParams, setSearchParams] = useSearchParams();
  const apiMessagesStatus =
    parseApiMessagesQueryStatus(searchParams.get("api_messages")) || "all";

  document.title = t("settings.apiTokens.documentTitle");

  const [items, setItems] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Histórico de revogados — off por padrão (lista limpa para Carla). */
  const [showRevoked, setShowRevoked] = useState(false);
  /** Curl longo — recolhido por padrão (menos ruído no primeiro olhar). */
  const [howToOpen, setHowToOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Token plain-text só neste estado — some ao fechar o modal. */
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [revealedName, setRevealedName] = useState<string>("");

  const [revoking, setRevoking] = useState<ApiToken | null>(null);
  const [revokingBusy, setRevokingBusy] = useState(false);
  /** Token destacado no curl de exemplo (filtro histórico ou clique). */
  const [curlTokenId, setCurlTokenId] = useState<number | null>(null);

  const helpHref = helpUrlForRoute("/settings/api-tokens", locale);

  const formatDate = (iso: string | null | undefined): string => {
    if (!iso) {
      return t("settings.apiTokens.neverUsed");
    }
    try {
      return new Date(iso).toLocaleString(locale);
    } catch {
      return iso;
    }
  };

  /** last_429 / campos opcionais — sem “Nunca usado”. */
  const formatOptionalDate = (iso: string | null | undefined): string => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(locale);
    } catch {
      return iso;
    }
  };

  const scopesLabel = (scopes: ApiToken["scopes"]): string => {
    const list = Array.isArray(scopes) ? scopes : [];
    if (list.length === 0) {
      return [
        t("settings.apiTokens.scopes.contacts"),
        t("settings.apiTokens.scopes.messages"),
      ].join(", ");
    }
    return list
      .map((s) =>
        t(`settings.apiTokens.scopes.${s}`, { defaultValue: String(s) })
      )
      .join(", ");
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getApiTokens({ include_revoked: showRevoked });
      if (!res?.success) {
        setError(t("settings.apiTokens.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
    } catch (e: any) {
      setError(e?.message || t("settings.apiTokens.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [t, showRevoked]);

  useEffect(() => {
    void load();
  }, [load]);

  // Deep-link ?api_messages= / ?api_token_id= → rola até o histórico.
  useEffect(() => {
    if (
      !searchParams.get("api_messages") &&
      !searchParams.get("api_token_id")
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      document
        .getElementById("api-messages-history-api")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchParams]);

  const formik = useFormik<FormValues>({
    initialValues: {
      name: "",
      scopeContacts: true,
      scopeMessages: true,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required(t("settings.apiTokens.form.nameRequired"))
        .max(255, t("settings.apiTokens.form.nameMax")),
      scopeContacts: Yup.boolean(),
      scopeMessages: Yup.boolean(),
    }),
    onSubmit: async (values) => {
      if (!values.scopeContacts && !values.scopeMessages) {
        toast.error(t("settings.apiTokens.form.scopesHint"), {
          position: "top-center",
        });
        return;
      }
      setSaving(true);
      try {
        const scopes: ApiTokenScope[] = [];
        if (values.scopeContacts) scopes.push("contacts");
        if (values.scopeMessages) scopes.push("messages");
        // Ambos = full — omitimos scopes no payload (contrato backend).
        const payload =
          scopes.length === 2
            ? { name: values.name.trim() }
            : { name: values.name.trim(), scopes };

        const res = await createApiToken(payload);
        if (!res?.success || !res.data?.plain_text_token) {
          toast.error(t("settings.apiTokens.error.create"), {
            position: "top-center",
          });
          return;
        }
        setCreateOpen(false);
        formik.resetForm();
        setRevealedName(res.data.name);
        setRevealedToken(res.data.plain_text_token);
        toast.success(res.message || t("settings.apiTokens.toast.created"), {
          position: "top-center",
        });
        await load();

        // Após revelar: filtra histórico do token novo + rola até a tabela.
        const createdId = res.data.id;
        if (createdId) {
          const next = new URLSearchParams(searchParams);
          next.set("api_token_id", String(createdId));
          setSearchParams(next, { replace: true });
          window.setTimeout(() => {
            document
              .getElementById("api-messages-history-api")
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 450);
        }
      } catch (e: any) {
        toast.error(e?.message || t("settings.apiTokens.error.create"), {
          position: "top-center",
        });
      } finally {
        setSaving(false);
      }
    },
  });

  const openCreate = () => {
    formik.resetForm();
    setCreateOpen(true);
  };

  const closeReveal = () => {
    setRevealedToken(null);
    setRevealedName("");
  };

  const copyToken = async () => {
    if (!revealedToken) {
      return;
    }
    try {
      await navigator.clipboard.writeText(revealedToken);
      toast.success(t("settings.apiTokens.reveal.copied"), {
        position: "top-center",
      });
    } catch {
      toast.error(t("settings.apiTokens.reveal.copyFailed"), {
        position: "top-center",
      });
    }
  };

  const confirmRevoke = async () => {
    if (!revoking) {
      return;
    }
    setRevokingBusy(true);
    try {
      const res = await revokeApiToken(revoking.id);
      toast.success(res?.message || t("settings.apiTokens.toast.revoked"), {
        position: "top-center",
      });
      setRevoking(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("settings.apiTokens.error.revoke"), {
        position: "top-center",
      });
    } finally {
      setRevokingBusy(false);
    }
  };

  const isEmpty = !loading && items.length === 0;

  const curlToken =
    items.find((tok) => tok.id === curlTokenId) ||
    items.find((tok) => !tok.revoked_at) ||
    null;
  const curlBearerHint = curlToken?.token_prefix
    ? `${curlToken.token_prefix.replace(/…$/, "")}…`
    : "SEU_TOKEN";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("settings.apiTokens.title")}
          actions={
            <PageHelpLink
              href={helpHref}
              label={t("settings.apiTokens.help")}
            />
          }
        />
        <IntegrationSettingsTabs />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <DismissiblePageTip tipId="api-tokens">
          <div>{t("settings.apiTokens.tip")}</div>
          <div className="mt-2 small">
            <code className="user-select-all">
              Authorization: Bearer be_token_…
            </code>
          </div>
          <div className="mt-2 small text-muted">
            {t("settings.apiTokens.tipRateLimit")}
          </div>
        </DismissiblePageTip>

        <div className="mb-3">
          <Button
            color="soft-info"
            type="button"
            onClick={() => setHowToOpen((v) => !v)}
            aria-expanded={howToOpen}
          >
            {t("settings.apiTokens.howToToggle")}
          </Button>
          <Collapse isOpen={howToOpen}>
            <Alert color="info" className="mb-0 mt-2">
              <div className="fw-medium mb-2">
                {t("settings.apiTokens.howToTitle")}
              </div>
              <div>
                {t("settings.apiTokens.tipMarketingUnsub")}{" "}
                <code className="user-select-all">
                  {"{{brenvio_track_unsub}}"}
                </code>
              </div>
              <div className="mt-2 small">
                <div className="fw-medium mb-1">
                  {t("settings.apiTokens.curlTitle")}
                  {curlToken ? (
                    <span className="text-muted fw-normal ms-2">
                      ({curlToken.name})
                    </span>
                  ) : null}
                </div>
                <pre
                  className="bg-light border rounded p-2 mb-0 small user-select-all"
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                >{`curl -X POST "https://api.brenvio.com.br/api/v1/messages" \\
  -H "Authorization: Bearer ${curlBearerHint}" \\
  -H "Content-Type: application/json" \\
  -d '{
  "to_email": "cliente@empresa.test",
  "subject": "Oferta",
  "message_type": "marketing",
  "html_body": "<p>Olá</p><p><a href=\\"{{brenvio_track_unsub}}\\">Cancelar inscrição</a></p>"
}'`}</pre>
                <div className="form-text mt-1">
                  {t("settings.apiTokens.curlPrefixHint")}
                </div>
              </div>
            </Alert>
          </Collapse>
        </div>

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-start">
                  <div>
                    <h5 className="card-title mb-1">
                      {t("settings.apiTokens.title")}
                    </h5>
                    <p className="text-muted mb-0">
                      {t("settings.apiTokens.subtitle")}
                    </p>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {/* Âncora quando a lista de tokens sobe — histórico fica abaixo. */}
                    {!loading && items.length >= 4 ? (
                      <Button
                        color="soft-secondary"
                        type="button"
                        onClick={() => {
                          document
                            .getElementById("api-messages-history-api")
                            ?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                        }}
                      >
                        {t("settings.apiTokens.jumpToHistory")}
                      </Button>
                    ) : null}
                    {!isEmpty && (
                      <Button color="primary" onClick={openCreate}>
                        {t("settings.apiTokens.create")}
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="form-check form-switch mb-3">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="api-tokens-show-revoked"
                    checked={showRevoked}
                    onChange={(e) => setShowRevoked(e.target.checked)}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="api-tokens-show-revoked"
                  >
                    {t("settings.apiTokens.showRevoked")}
                  </Label>
                </div>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : isEmpty ? (
                  <div className="text-center py-5">
                    <div className="avatar-md mx-auto mb-3">
                      <div className="avatar-title bg-soft-primary text-primary rounded-circle fs-2">
                        <i className="ri-key-2-line" />
                      </div>
                    </div>
                    <h5>{t("settings.apiTokens.empty.title")}</h5>
                    <p className="text-muted mb-3">
                      {t("settings.apiTokens.empty.description")}
                    </p>
                    <Button color="primary" onClick={openCreate}>
                      {t("settings.apiTokens.empty.cta")}
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-nowrap align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("settings.apiTokens.table.name")}</th>
                          <th>{t("settings.apiTokens.table.prefix")}</th>
                          <th>{t("settings.apiTokens.table.scopes")}</th>
                          <th>{t("settings.apiTokens.table.rateLimit")}</th>
                          <th>{t("settings.apiTokens.table.lastUsed")}</th>
                          <th>{t("settings.apiTokens.table.last429")}</th>
                          <th>{t("settings.apiTokens.table.lastFailure")}</th>
                          <th>{t("settings.apiTokens.table.status")}</th>
                          <th className="text-end">
                            {t("settings.apiTokens.table.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((token) => {
                          const active = !token.revoked_at;
                          const rl = token.rate_limit;
                          return (
                            <tr
                              key={token.id}
                              role="button"
                              style={{ cursor: "pointer" }}
                              onClick={() => setCurlTokenId(token.id)}
                              className={
                                curlTokenId === token.id
                                  ? "table-active"
                                  : undefined
                              }
                            >
                              <td className="fw-medium">{token.name}</td>
                              <td>
                                <code className="small text-muted">
                                  {token.token_prefix || "be_token_…"}
                                </code>
                              </td>
                              <td className="small">{scopesLabel(token.scopes)}</td>
                              <td className="small text-muted">
                                {rl
                                  ? t("settings.apiTokens.rateLimitCell", {
                                      remaining: rl.remaining,
                                      limit: rl.per_minute,
                                    })
                                  : "—"}
                              </td>
                              <td>{formatDate(token.last_used_at)}</td>
                              <td className="small text-muted">
                                {formatOptionalDate(token.last_429_at)}
                              </td>
                              <td className="small">
                                {token.last_failure ? (
                                  <span
                                    title={
                                      token.last_failure.skip_reason ||
                                      undefined
                                    }
                                  >
                                    {formatDate(token.last_failure.at)}
                                    <div
                                      className="text-muted text-truncate"
                                      style={{ maxWidth: 140 }}
                                    >
                                      {token.last_failure.to_email}
                                      {token.last_failure.skip_reason
                                        ? ` · ${skipReasonLabel(token.last_failure.skip_reason, t)}`
                                        : ""}
                                    </div>
                                  </span>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td>
                                {active ? (
                                  <Badge
                                    color="success"
                                    className="text-uppercase"
                                  >
                                    {t("settings.apiTokens.status.active")}
                                  </Badge>
                                ) : (
                                  <Badge
                                    color="secondary"
                                    className="text-uppercase"
                                  >
                                    {t("settings.apiTokens.status.revoked")}
                                  </Badge>
                                )}
                              </td>
                              <td className="text-end">
                                {active ? (
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRevoking(token);
                                    }}
                                  >
                                    {t("settings.apiTokens.revoke")}
                                  </Button>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Mesma tela dos tokens: quem integra vê falhas sem ir a Conta. */}
            <ApiMessageHistoryCard
              source="api"
              titleKey="apiMessages.history.apiTitle"
              hintKey="apiMessages.history.apiHint"
              notifyFailed
              initialStatus={apiMessagesStatus}
              syncTokenToUrl
              onTokenFilterChange={setCurlTokenId}
              tokenOptions={items.map((tok) => ({
                id: tok.id,
                name: tok.name,
                token_prefix: tok.token_prefix,
              }))}
              emptyHintKey={
                isEmpty
                  ? "apiMessages.history.emptyNoToken"
                  : "apiMessages.history.empty"
              }
              emptyCtaLabelKey={
                isEmpty ? "settings.apiTokens.empty.cta" : undefined
              }
              onEmptyCta={isEmpty ? openCreate : undefined}
            />
          </Col>
        </Row>
      </Container>

      {/* Modal criar — nome + escopos (contacts / messages). */}
      <Modal
        isOpen={createOpen}
        toggle={() => !saving && setCreateOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !saving && setCreateOpen(false)}>
          {t("settings.apiTokens.form.title")}
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="api-token-name">
                {t("settings.apiTokens.form.name")}
              </Label>
              <Input
                id="api-token-name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.name && formik.errors.name)}
                placeholder={t("settings.apiTokens.form.namePlaceholder")}
                autoFocus
              />
              {formik.touched.name && formik.errors.name && (
                <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
              )}
              <div className="form-text">
                {t("settings.apiTokens.form.nameHint")}
              </div>
            </div>
            <div className="mb-0">
              <Label className="d-block">
                {t("settings.apiTokens.table.scopes")}
              </Label>
              <div className="form-check">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="api-token-scope-contacts"
                  checked={formik.values.scopeContacts}
                  onChange={(e) =>
                    formik.setFieldValue("scopeContacts", e.target.checked)
                  }
                />
                <Label
                  className="form-check-label"
                  htmlFor="api-token-scope-contacts"
                >
                  {t("settings.apiTokens.scopes.contacts")}
                </Label>
              </div>
              <div className="form-check">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="api-token-scope-messages"
                  checked={formik.values.scopeMessages}
                  onChange={(e) =>
                    formik.setFieldValue("scopeMessages", e.target.checked)
                  }
                />
                <Label
                  className="form-check-label"
                  htmlFor="api-token-scope-messages"
                >
                  {t("settings.apiTokens.scopes.messages")}
                </Label>
              </div>
              <div className="form-text">
                {t("settings.apiTokens.form.scopesHint")}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              type="button"
              disabled={saving}
              onClick={() => setCreateOpen(false)}
            >
              {t("settings.apiTokens.form.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : null}
              {t("settings.apiTokens.form.submit")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Modal revelação — plain-text 1× (não volta a aparecer). */}
      <Modal
        isOpen={!!revealedToken}
        toggle={closeReveal}
        centered
        backdrop="static"
      >
        <ModalHeader toggle={closeReveal}>
          {t("settings.apiTokens.reveal.title")}
        </ModalHeader>
        <ModalBody>
          <Alert color="warning" className="mb-3">
            {t("settings.apiTokens.reveal.warning")}
          </Alert>
          {revealedName ? (
            <p className="text-muted mb-2">
              {t("settings.apiTokens.reveal.forName", { name: revealedName })}
            </p>
          ) : null}
          <Label>{t("settings.apiTokens.reveal.label")}</Label>
          <div className="input-group">
            <Input
              type="text"
              readOnly
              value={revealedToken || ""}
              className="font-monospace"
              aria-label={t("settings.apiTokens.reveal.label")}
            />
            <Button
              color="primary"
              type="button"
              onClick={() => void copyToken()}
            >
              <i className="ri-file-copy-line me-1" />
              {t("settings.apiTokens.reveal.copy")}
            </Button>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={closeReveal}>
            {t("settings.apiTokens.reveal.done")}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirmação de revogação — quebra integrações ativas. */}
      <Modal
        isOpen={!!revoking}
        toggle={() => !revokingBusy && setRevoking(null)}
        centered
      >
        <ModalHeader toggle={() => !revokingBusy && setRevoking(null)}>
          {t("settings.apiTokens.confirmRevoke.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {t("settings.apiTokens.confirmRevoke.body", {
              name: revoking?.name || "",
            })}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={revokingBusy}
            onClick={() => setRevoking(null)}
          >
            {t("settings.apiTokens.confirmRevoke.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={revokingBusy}
            onClick={() => void confirmRevoke()}
          >
            {revokingBusy ? <Spinner size="sm" className="me-1" /> : null}
            {t("settings.apiTokens.confirmRevoke.confirm")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ApiTokensPage;
